/**
 * Stable key for a party slot (per ADR-0044). Used everywhere signatories
 * are grouped by slot — the signing-block atoms, the render payload
 * builder, and the gallery's sample roster — so the string format lives
 * in exactly one place.
 */
export function slotKey(role, ordinal) {
    return `${role}:${ordinal}`;
}
/** Roles that sign when no template slot says otherwise. */
const DEFAULT_SIGNING_ROLES = new Set(['lessor', 'lessee', 'guarantor']);
/**
 * Whether a contract party signs. The template's matching party slot is
 * authoritative (`signs !== false`), so an `other` slot that signs — e.g.
 * the fastighetsägarförening in a three-party förhandlingsordning — is a
 * signer, and a lessee slot marked `signs: false` is not. Parties without a
 * matching slot (or no template) fall back to the lessor/lessee/guarantor
 * role default.
 */
export function isSigningParty(metadata, role, ordinal) {
    const slot = metadata?.partySlots.find((s) => s.role === role && s.ordinal === ordinal);
    if (slot)
        return slot.signs !== false;
    return DEFAULT_SIGNING_ROLES.has(role);
}
/**
 * Build signing-block refs from a template's `partySlots`. Convenience for
 * templates whose signing block lists slots verbatim (label = slot label,
 * declaration order). Pass `selectors` to choose a subset / custom order /
 * custom printed labels — anything beyond the straightforward case
 * (conditional co-tenants, a lessor who signs twice, a slot printed under
 * a different caption) is better written as an explicit ref array.
 */
export function signingRefs(metadata, selectors) {
    if (!selectors) {
        return metadata.partySlots
            .filter((s) => s.signs !== false)
            .map((s) => ({ label: s.signingLabel ?? s.label, role: s.role, ordinal: s.ordinal }));
    }
    return selectors.map((sel) => {
        const slot = metadata.partySlots.find((s) => s.role === sel.role && s.ordinal === sel.ordinal);
        return {
            label: sel.label ?? slot?.signingLabel ?? slot?.label ?? `${sel.role} ${sel.ordinal}`,
            role: sel.role,
            ordinal: sel.ordinal,
        };
    });
}
//# sourceMappingURL=signing-refs.js.map